import { atom } from "nanostores";
import { listMarkers, type MarkerView } from "../net/api";
import { clip } from "../world/markers";
import { $myPlots, $panel, toast } from "./stores";

/** Markers of the plots this person owns or builds on, by plot id (spec 2h §2.4). */
export const $markers = atom<Record<string, MarkerView[]>>({});
/** Plots whose markers could not be read; marker mode refuses there instead of guessing the count. */
export const $markersFailed = atom<string[]>([]);

export type MarkerPanelState =
  | { mode: "create"; plotId: string; plotName: string; pos: [number, number, number]; at: { x: number; y: number; z: number };
      target: { entity_id: string; name: string } | null }
  | { mode: "view"; plotId: string; id: string };
/** What the MarkerPanel shows; "create" also places the preview pin at `at`. */
export const $markerPanel = atom<MarkerPanelState | null>(null);
/** The pin under the crosshair, for "[E] 開啟標記". */
export const $lookMarker = atom<{ plotId: string; id: string } | null>(null);

export function doneSince(prev: MarkerView[], next: MarkerView[]): MarkerView[] {
  const was = new Map(prev.map((m) => [m.id, m.status]));
  return next.filter((m) => m.status === "done" && was.get(m.id) === "open");
}

const readSeq: Record<string, number> = {};

/** Re-reads one plot; announces markers an agent finished since the last read (spec 2h §2.6). */
export async function loadMarkers(plotId: string): Promise<void> {
  const mine = (readSeq[plotId] = (readSeq[plotId] ?? 0) + 1);
  try {
    const list = await listMarkers(plotId);
    if (readSeq[plotId] !== mine) return; // a newer read is on its way or already applied
    const prev = $markers.get()[plotId];
    if (prev) for (const m of doneSince(prev, list)) toast(`標記已完成：${clip(m.reply ?? "", 24)}`, 5000);
    $markers.set({ ...$markers.get(), [plotId]: list });
    $markersFailed.set($markersFailed.get().filter((id) => id !== plotId));
  } catch {
    if (readSeq[plotId] !== mine) return;
    if (!$markersFailed.get().includes(plotId)) $markersFailed.set([...$markersFailed.get(), plotId]);
  }
}

/** Reads every plot with a role and forgets plots without one (Welcome and PlotsChanged). */
export async function loadAllMarkers(): Promise<void> {
  const ids = $myPlots.get().map((p) => p.id);
  const kept: Record<string, MarkerView[]> = {};
  for (const id of ids) { const list = $markers.get()[id]; if (list) kept[id] = list; }
  $markers.set(kept);
  $markersFailed.set($markersFailed.get().filter((id) => ids.includes(id)));
  await Promise.all(ids.map(loadMarkers));
}

export function upsertMarker(plotId: string, m: MarkerView): void {
  const list = $markers.get()[plotId] ?? [];
  const next = list.some((x) => x.id === m.id) ? list.map((x) => (x.id === m.id ? m : x)) : [...list, m];
  $markers.set({ ...$markers.get(), [plotId]: next });
}

export function dropMarker(plotId: string, id: string): void {
  const list = $markers.get()[plotId];
  if (list) $markers.set({ ...$markers.get(), [plotId]: list.filter((x) => x.id !== id) });
}

/** E on a pin: show that marker (spec 2h §2.5). */
export function openMarker(ref: { plotId: string; id: string }): void {
  $markerPanel.set({ mode: "view", plotId: ref.plotId, id: ref.id });
  $panel.set("marker");
}
